import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Select } from '@ann/web-components';
import { SelectorContainer } from './DashboardSelector.styled';

/**
 * Dropdown to switch between available dashboards.
 * Hidden when there's only one dashboard (nothing to switch to).
 */
const DashboardSelector = ({ dashboards, selectedUuid, onSelect, loading }) => {
  const options = useMemo(
    () =>
      dashboards.map((d) => ({
        label: d.name,
        value: d.dashboard_uuid,
      })),
    [dashboards],
  );

  const selectedValue = useMemo(
    () => options.find((o) => o.value === selectedUuid) || null,
    [options, selectedUuid],
  );

  const handleChange = useCallback(
    (option) => {
      if (option?.value && option.value !== selectedUuid) {
        onSelect?.(option.value);
      }
    },
    [onSelect, selectedUuid],
  );

  if (dashboards.length <= 1) return null;

  return (
    <SelectorContainer>
      <Select
        options={options}
        value={selectedValue}
        onChange={handleChange}
        size="small"
        isDisabled={loading}
        placeholder="Select dashboard..."
        isSearchable={false}
      />
    </SelectorContainer>
  );
};

DashboardSelector.propTypes = {
  dashboards: PropTypes.arrayOf(
    PropTypes.shape({
      dashboard_uuid: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  selectedUuid: PropTypes.string,
  onSelect: PropTypes.func,
  loading: PropTypes.bool,
};

DashboardSelector.defaultProps = {
  dashboards: [],
  selectedUuid: null,
  onSelect: undefined,
  loading: false,
};

export default DashboardSelector;
